type DefaultEnumValueType = string | number;

export interface EnumValue<KeyType extends string = string, ValueType = DefaultEnumValueType> {
    name: KeyType;
    value: ValueType;
    toString(): string;
}

export type Enumify<KeyType extends string = string, ValueType = DefaultEnumValueType> = {
    [key in KeyType]: EnumValue<KeyType, ValueType>;
} & {
    enumValues: Array<EnumValue<KeyType, ValueType>>;
    getByName(name: KeyType): EnumValue<KeyType, ValueType>;
    getByValue(value: ValueType): EnumValue<KeyType, ValueType> | undefined;
};

export type EnumConfig<KeyType extends string = string, ValueType = DefaultEnumValueType> = {
    [key in KeyType]: ValueType;
};

export function enumify<KeyType extends string = string, ValueType = DefaultEnumValueType>(
    enumeration: () => void,
    enumName: string,
    values: EnumConfig<KeyType, ValueType>
) {
    if (typeof enumeration !== 'function') {
        throw new Error('Expected the enumeration to be a function.');
    }

    if (enumName.length === 0) {
        throw new Error("Expected the enumeration's name to be a non-empty string.");
    }

    if (!isPlainObject(values)) {
        throw TypeError('Values of enumeration expected as object');
    }

    const enumValues = initEnumValues(values);
    const enumProperties = keyBy(enumValues, 'name');

    Object.assign(enumeration, enumProperties, {
        enumValues,

        getByName(name: KeyType) {
            return enumValues.find((v) => v.name === name);
        },

        getByValue(value: ValueType) {
            return enumValues.find((x) => x.value === value);
        },

        toString() {
            return enumName;
        },
    });

    Object.freeze(enumeration);

    function initEnumValues(args: EnumConfig<KeyType, ValueType>) {
        return Object.keys(args).map((name) => {
            // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'EnumConfig<KeyType...
            const value = args[name];
            let mapped = {};

            if (Object.prototype.hasOwnProperty.call(value, 'value')) {
                // eslint-disable-next-line prefer-object-spread
                mapped = Object.assign({ name }, value);
            } else {
                mapped = {
                    name,
                    value,
                };
            }

            mapped.toString = function toString() {
                return enumName + name;
            };

            return create(enumeration, mapped);
        });
    }
}

function create(type: () => void, ...mixins: $TSFixMe[]) {
    return Object.assign(Object.create(type.prototype), ...mixins);
}

function isObjectLike(value: {}) {
    return typeof value === 'object' && value !== null;
}

function getStringTag(value: {}) {
    return Object.prototype.toString.call(value);
}

function isPlainObject(value: {}) {
    if (!isObjectLike(value) || getStringTag(value) !== '[object Object]') {
        return false;
    }

    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}

function keyBy<T extends object>(collection: T, property: string) {
    return Object.keys(collection).reduce((result, valueKey) => {
        // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'unknown'.
        const value = collection[valueKey];

        const key = value[property];
        // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
        result[key] = value;
        return result;
    }, {});
}
